import clsx from 'clsx';
import type { CSSProperties } from 'react';

import DrupalImage from '@commons/components/Image/Drupal';
import type { DrupalBlock, DrupalImage as DrupalImageType } from '@commons/types/drupal';

// Type definition for the ImageInlineBlock component properties.

type ImageInlineBlockProps = DrupalBlock<{
  image: DrupalImageType;
  priority?: boolean;
  fullWidth?: boolean;
  roundCorners?: boolean;
}>;

/**
 * Image inline block component.
 */

export const ImageInlineBlock = ({ content }: ImageInlineBlockProps) => {
  const { image, priority = false, fullWidth = false, roundCorners = false } = content;

  // Fallback in case Drupal does not return an image.
  if (!image?.url) return null;

  const classes = clsx(fullWidth && 'w-100');

  // Calculate the aspect ratio based on the image dimensions.
  // This is required to avoid layout shifts and to ensure that anchors scroll to the correct position.

  const inlineStyles: CSSProperties = {
    aspectRatio: `${image.width} / ${image.height}`,
    borderRadius: roundCorners ? 'var(--corner-border-radius)' : 0,
  };

  return <DrupalImage {...image} priority={priority} className={classes} style={inlineStyles} />;
};

/**
 * Image field block component.
 *
 * Defined as a separate component as it does not have the same data structure as the inline block.
 */

export const ImageFieldBlock = ({ content }: DrupalBlock<DrupalImageType>) => (
  <DrupalImage {...content} />
);
